/**
 * Auth state for the staff SPA.
 *
 * `completeSsoLogin` is the single most bug-prone function in any platform app. The integration
 * spec calls it out as pitfall #1: if it only writes the JWT to localStorage and calls
 * `fetchUser()`, global state still has `isAuthenticated === false`, so ProtectedRoute silently
 * bounces the user back to `/login` after a *successful* IdP sign-in, with no visible error.
 *
 * It therefore does all three steps, **in this order**:
 *   1. persist the token (for the axios interceptor and reload survival)
 *   2. set `isAuthenticated: true` (so ProtectedRoute lets the user through)
 *   3. hydrate the user from `/auth/me`
 *
 * Step 2 must precede the caller's `navigate()`, or the navigation races a re-render that can
 * unmount the callback page mid-flight.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  clearToken,
  extractDetail,
  getToken,
  registerAuthHandlers,
  setToken,
} from '@/services/api';
import {
  fetchCurrentUser,
  loginWithPassword,
  logout as logoutRequest,
  refreshPermissions as refreshPermissionsRequest,
} from '@/services/auth';
import type { CurrentUser } from '@/types';

interface AuthState {
  user: CurrentUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Set when Bedrock reports `app_not_allowed` — render the access-denied screen, not /login. */
  accessDenied: string | null;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  completeSsoLogin: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<CurrentUser>;
  refreshPermissions: () => Promise<void>;
  clearAccessDenied: () => void;
  /** Permission check. Accepts prefixed or unprefixed keys; local admin always passes. */
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  isAdmin: boolean;
  isLocalAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INITIAL: AuthState = {
  user: null,
  token: getToken(),
  isAuthenticated: false,
  isLoading: true,
  error: null,
  accessDenied: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL);
  const queryClient = useQueryClient();
  /** Guards the bootstrap effect against React StrictMode's double-mount in dev. */
  const bootstrapped = useRef(false);

  const patch = useCallback((next: Partial<AuthState>) => {
    setState((prev) => ({ ...prev, ...next }));
  }, []);

  const fetchUser = useCallback(async (): Promise<CurrentUser> => {
    const user = await fetchCurrentUser();
    patch({ user, isAuthenticated: true, isLoading: false, error: null });
    return user;
  }, [patch]);

  /** Local sign-out: drop the token and every cached query keyed to this identity. */
  const resetSession = useCallback(() => {
    clearToken();
    queryClient.clear();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      accessDenied: null,
    });
  }, [queryClient]);

  // Wire the axios interceptor's callbacks once.
  useEffect(() => {
    registerAuthHandlers({
      onUnauthenticated: () => resetSession(),
      onForbiddenApp: (message) => {
        clearToken();
        setState((prev) => ({
          ...prev,
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          accessDenied: message,
        }));
      },
    });
  }, [resetSession]);

  // Restore a session on load / hard refresh (verification item 14: refresh stays logged in).
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const token = getToken();
    if (!token) {
      patch({ isLoading: false });
      return;
    }

    fetchUser().catch(() => {
      // The interceptor has already cleared the token and reset state for 401/403.
      patch({ isLoading: false });
    });
  }, [fetchUser, patch]);

  const login = useCallback(
    async (username: string, password: string) => {
      patch({ error: null, accessDenied: null });
      try {
        const result = await loginWithPassword(username, password);
        setToken(result.access_token);
        patch({ token: result.access_token, isAuthenticated: true });
        await fetchUser();
      } catch (error) {
        const message = extractDetail(error);
        patch({ error: message, isLoading: false });
        throw error;
      }
    },
    [fetchUser, patch],
  );

  const completeSsoLogin = useCallback(
    async (token: string) => {
      // 1. Persist for axios interceptors and reload survival.
      setToken(token);
      // 2. Update global state so ProtectedRoute lets the user through. THIS is the step every
      //    app forgets; without it the user lands back on /login with no error shown.
      setState((prev) => ({
        ...prev,
        token,
        isAuthenticated: true,
        error: null,
        accessDenied: null,
      }));
      // 3. Hydrate the user object. A failure here surfaces as an error on the callback page
      //    rather than a silent bounce.
      await fetchUser();
    },
    [fetchUser],
  );

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // A failed server-side cache purge must never trap the user in a session.
    }
    resetSession();
  }, [resetSession]);

  const refreshPermissions = useCallback(async () => {
    const user = await refreshPermissionsRequest();
    patch({ user });
    // Permission-gated queries — and the grid menu's scope — depend on identity.
    await queryClient.invalidateQueries({ queryKey: ['app-directory'] });
  }, [patch, queryClient]);

  const value = useMemo<AuthContextValue>(() => {
    const slug = state.user?.app_slug ?? '';
    const permissions = new Set(state.user?.permissions ?? []);

    const hasPermission = (permission: string): boolean => {
      // The break-glass admin implicitly holds every permission inside this app.
      if (state.user?.is_local_admin) return true;
      const key = slug && !permission.startsWith(`${slug}.`) ? `${slug}.${permission}` : permission;
      return permissions.has(key);
    };

    return {
      ...state,
      login,
      completeSsoLogin,
      logout,
      fetchUser,
      refreshPermissions,
      clearAccessDenied: () => patch({ accessDenied: null }),
      hasPermission,
      hasAnyPermission: (...keys: string[]) => keys.some(hasPermission),
      // "Admin" for UI-gating purposes means the registration/settings capability. The API
      // enforces every permission independently — hiding UI is never the control.
      isAdmin: hasPermission('admin.users.manage') || hasPermission('admin.settings'),
      isLocalAdmin: Boolean(state.user?.is_local_admin),
    };
  }, [state, login, completeSsoLogin, logout, fetchUser, refreshPermissions, patch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
